import {
  lazy,
  Suspense,
  useEffect,
  useContext,
  useState,
  useSyncExternalStore,
} from 'react'
import { showAppToast } from '../../../shared/ui/app-toast-controller'
import { useOverlayBlocked } from '../../../shared/ui/overlay-activity'
import { browserTokenStorage } from '../../../platform/auth/browser-token-storage'
import { StoryFailure } from '../domain/story'
import { RecommendationRuntime } from '../application/recommendation-runtime'
import { RecommendationAccessContext } from './recommendation-access-context'
import { recommendationsApi } from '../infrastructure/recommendations-api'
import { StoriesRuntime } from '../application/stories-runtime'
import type { StoryTransport } from '../application/ports'
import { engagementApi } from '../infrastructure/engagement-api'
import {
  createPendingStorage,
  createStoryClock,
  observeStoryBrowser,
  storyPlatform,
  storyTimezone,
  toStoryFailure,
} from '../infrastructure/browser-adapters'
import { safeStoryPath } from '../infrastructure/story-target'
import { StoryViewerBoundary } from './StoryViewerBoundary'
import { ManualMarketingRuntime } from '../application/manual-marketing-runtime'
import { MarketingAccessContext } from './marketing-access-context'
import { useMarketingOffers } from './use-marketing-offers'

const RecommendationStoryDialog = lazy(
  () => import('./RecommendationStoryDialog'),
)
const MarketingStoryDialog = lazy(() => import('./MarketingStoryDialog'))
const ManualMarketingDialog = lazy(() => import('./ManualMarketingDialog'))
export type StoryNavigationType = 'spa' | 'document' | null
type Props = {
  account: string
  accessToken: string
  inCabinet: boolean
  allowed: boolean
  navigationKey?: string
  manualAllowed?: boolean
  navigationType: (path: string) => StoryNavigationType
  navigate: (path: string, type: Exclude<StoryNavigationType, null>) => void
}

export function StoriesHost({
  account,
  accessToken,
  inCabinet,
  allowed,
  manualAllowed = allowed,
  navigationKey,
  navigationType,
  navigate,
}: Props) {
  const [visit] = engagementApi.useStoryVisitMutation()
  const [decide] = recommendationsApi.useUnifiedStoryDecisionMutation()
  const [impression] = engagementApi.useStoryImpressionMutation()
  const [interact] = engagementApi.useStoryInteractionMutation()
  const [reserveManualMarketing] =
    engagementApi.useManualMarketingStoryMutation()
  const [clock] = useState(createStoryClock)
  const offers = useMarketingOffers(account, clock)
  const [manual] = recommendationsApi.useRecommendationManualMutation()
  const [activate] = recommendationsApi.useRecommendationActivateMutation()
  const [next] = recommendationsApi.useRecommendationNextMutation()
  const [recImpression] =
    recommendationsApi.useRecommendationImpressionMutation()
  const [recInteraction] =
    recommendationsApi.useRecommendationInteractionMutation()
  const catalog = recommendationsApi.useRecommendationCatalogQuery(account)
  const { setAccess } = useContext(RecommendationAccessContext)
  const { setAccess: setMarketingAccess } = useContext(MarketingAccessContext)
  const [recommendations] = useState(() => {
    const guard = async <T,>(call: () => Promise<T>) => {
      if (browserTokenStorage.read() !== accessToken)
        throw new StoryFailure('contract', 'STORY_SESSION_CHANGED')
      try {
        return await call()
      } catch (error) {
        throw toStoryFailure(error)
      }
    }
    return new RecommendationRuntime({
      transport: {
        manual: (c) => guard(() => manual(c).unwrap()),
        activate: (c) => guard(() => activate(c).unwrap()),
        next: (c) => guard(() => next(c).unwrap()),
      },
      receipts: {
        visit: () => Promise.reject(new Error('Unsupported')),
        decide: () => Promise.reject(new Error('Unsupported')),
        impression: (c) => guard(() => recImpression(c).unwrap()),
        interact: (c) => guard(() => recInteraction(c).unwrap()),
      },
      onManualError: () =>
        showAppToast(
          'Не удалось открыть рекомендации. Попробуйте ещё раз.',
          'error',
        ),
      storage: createPendingStorage('cardreplay:recommendation-pending:v1'),
      clock,
      uuid: () => crypto.randomUUID(),
      platform: storyPlatform(),
      canNavigate: (path) =>
        safeStoryPath(path) !== null && /^\/guide#[a-z0-9-]+$/.test(path),
      navigate: (path) => navigate(path, 'spa'),
    })
  })
  const recSnapshot = useSyncExternalStore(
    recommendations.subscribe,
    recommendations.getSnapshot,
  )
  const [manualMarketing] = useState(() => {
    const guard = async <T,>(call: () => Promise<T>) => {
      if (browserTokenStorage.read() !== accessToken)
        throw new StoryFailure('contract', 'STORY_SESSION_CHANGED')
      try {
        return await call()
      } catch (error) {
        throw toStoryFailure(error)
      }
    }
    return new ManualMarketingRuntime({
      reserve: (command) =>
        guard(() => reserveManualMarketing(command).unwrap()),
      receipts: {
        visit: () => Promise.reject(new Error('Unsupported')),
        decide: () => Promise.reject(new Error('Unsupported')),
        impression: (command) => guard(() => impression(command).unwrap()),
        interact: (command) => guard(() => interact(command).unwrap()),
      },
      sourceBusy: recommendations.isActive,
      storage: createPendingStorage('cardreplay:marketing-manual-pending:v1'),
      clock,
      uuid: () => crypto.randomUUID(),
      platform: storyPlatform(),
      canNavigate: (path) =>
        safeStoryPath(path) !== null && navigationType(path) !== null,
      navigate: (path) => {
        const type = navigationType(path)
        if (type) navigate(path, type)
      },
      onError: () =>
        showAppToast('Не удалось открыть акции. Попробуйте ещё раз.', 'error'),
    })
  })
  const manualSnapshot = useSyncExternalStore(
    manualMarketing.subscribe,
    manualMarketing.getSnapshot,
  )
  const blocked = useOverlayBlocked('engagement-story')
  const [visible, setVisible] = useState(
    () => document.visibilityState === 'visible',
  )
  const [runtime] = useState(() => {
    const guard = async <T,>(call: () => Promise<T>) => {
      if (browserTokenStorage.read() !== accessToken)
        throw new StoryFailure('contract', 'STORY_SESSION_CHANGED')
      try {
        return await call()
      } catch (error) {
        throw toStoryFailure(error)
      }
    }
    const transport: StoryTransport = {
      visit: (command) => guard(() => visit(command).unwrap()),
      decide: (command) => guard(() => decide(command).unwrap()),
      impression: (command) => guard(() => impression(command).unwrap()),
      interact: (command) => guard(() => interact(command).unwrap()),
    }
    return new StoriesRuntime({
      transport,
      sourceBusy: () =>
        recommendations.isActive() || manualMarketing.isActive(),
      onPresencePending: () => {
        recommendations.pausePresence()
        manualMarketing.pausePresence()
      },
      onVisit: (client, visit) => {
        recommendations.setVisit(client, visit)
        manualMarketing.setVisit(client, visit)
      },
      onRecommendations: (batch, client) =>
        recommendations.start(batch, client),
      storage: createPendingStorage(),
      clock,
      uuid: () => crypto.randomUUID(),
      platform: storyPlatform(),
      timezone: storyTimezone(),
      canNavigate: (path) =>
        safeStoryPath(path) !== null && navigationType(path) !== null,
      navigate: (path) => {
        const type = navigationType(path)
        if (type) navigate(path, type)
      },
    })
  })
  const snapshot = useSyncExternalStore(runtime.subscribe, runtime.getSnapshot)
  const automaticBusy = runtime.isActive()

  useEffect(() => {
    recommendations.update({
      account,
      visible,
      inCabinet,
      allowed,
      manualAllowed,
      blocked: blocked || manualMarketing.isActive(),
    })
    manualMarketing.update({
      account,
      visible,
      inCabinet,
      allowed,
      manualAllowed,
      blocked:
        blocked || recommendations.isActive() || !!runtime.getSnapshot().story,
    })
    runtime.update({
      account,
      visible,
      inCabinet,
      allowed,
      blocked,
      ...(navigationKey ? { navigationKey } : {}),
    })
  }, [
    runtime,
    recommendations,
    account,
    visible,
    inCabinet,
    allowed,
    blocked,
    recSnapshot.batch,
    recSnapshot.busy,
    manualMarketing,
    manualSnapshot.busy,
    manualSnapshot.decision,
    snapshot.story,
    automaticBusy,
    manualAllowed,
    navigationKey,
  ])

  useEffect(() => {
    const ids = (offers.currentData?.value ?? [])
      .filter((item) => item.endsAt > clock.now())
      .map((item) => item.id)
    setMarketingAccess({
      account,
      available: ids.length > 0,
      ready:
        manualSnapshot.ready &&
        !manualMarketing.isActive() &&
        !automaticBusy &&
        !recommendations.isActive(),
      isCurrent: () => browserTokenStorage.read() === accessToken,
      open: () => {
        if (!runtime.isActive()) manualMarketing.open(ids)
      },
    })
    return () => setMarketingAccess(null)
  }, [
    setMarketingAccess,
    account,
    accessToken,
    offers.currentData,
    manualMarketing,
    manualSnapshot.ready,
    manualSnapshot.busy,
    manualSnapshot.decision,
    recommendations,
    recSnapshot.busy,
    recSnapshot.batch,
    runtime,
    snapshot.story,
    automaticBusy,
    clock,
  ])

  useEffect(
    () =>
      observeStoryBrowser(
        () => {
          const next = document.visibilityState === 'visible'
          runtime.setVisibility(next)
          setVisible(next)
        },
        () => {
          const next = document.visibilityState === 'visible'
          runtime.setVisibility(next)
          setVisible(next)
          runtime.wake()
        },
      ),
    [runtime],
  )

  useEffect(
    () => runtime.attach(() => browserTokenStorage.read() !== accessToken),
    [runtime, accessToken],
  )

  useEffect(() => {
    setAccess({
      account,
      catalog: catalog.currentData ?? null,
      isCurrent: () => browserTokenStorage.read() === accessToken,
      ready: recSnapshot.ready && !snapshot.story,
      busy: recSnapshot.busy || recSnapshot.batch !== null,
      requestOpen: () => recommendations.requestManual(),
      open: (id) => recommendations.manual(id),
    })
    return () => setAccess(null)
  }, [
    setAccess,
    account,
    accessToken,
    catalog.currentData,
    recommendations,
    recSnapshot.ready,
    recSnapshot.busy,
    manualAllowed,
    recSnapshot.batch,
    snapshot.story,
  ])
  useEffect(
    () => () =>
      recommendations.destroy(browserTokenStorage.read() !== accessToken),
    [recommendations, accessToken],
  )
  useEffect(
    () => () =>
      manualMarketing.destroy(browserTokenStorage.read() !== accessToken),
    [manualMarketing, accessToken],
  )
  if (manualSnapshot.decision)
    return (
      <StoryViewerBoundary
        key={manualSnapshot.decision.reservationId}
        onUnavailable={() => manualMarketing.unavailable()}
      >
        <Suspense fallback={null}>
          <ManualMarketingDialog
            snapshot={manualSnapshot}
            runtime={manualMarketing}
          />
        </Suspense>
      </StoryViewerBoundary>
    )
  if (recSnapshot.batch?.current)
    return (
      <StoryViewerBoundary
        key={recSnapshot.batch.id}
        onUnavailable={() => recommendations.unavailable()}
      >
        <Suspense fallback={null}>
          <RecommendationStoryDialog
            batch={recSnapshot.batch}
            open={recSnapshot.visible}
            onDisplayed={(id) => recommendations.displayed(id)}
            onPrevious={() => recommendations.previous()}
            onAdvance={(reason) => recommendations.advance(reason)}
            onFinish={() => recommendations.advance('timer', true)}
            onClose={() => recommendations.act('close')}
            onCta={() => recommendations.act('cta')}
          />
        </Suspense>
      </StoryViewerBoundary>
    )
  if (!snapshot.story) return null
  const decision = snapshot.story
  return (
    <StoryViewerBoundary
      key={decision.reservationId}
      onUnavailable={() => runtime.finishViewing(decision.reservationId)}
    >
      <Suspense fallback={null}>
        <MarketingStoryDialog
          decision={decision}
          open={snapshot.visible}
          onFinish={() => runtime.act('completed')}
          onClose={() => runtime.act('close')}
          onCta={() => runtime.act('cta')}
          onDisplayed={() => runtime.displayed(decision.reservationId)}
        />
      </Suspense>
    </StoryViewerBoundary>
  )
}
